import React, { useState, useEffect, useRef, useMemo, useId } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { useData } from "../../context/DataContext";
import { Qualification } from "../../types";
import { SYSTEM_ROLES, FIELD_ROLES } from "../../utils/user-logic";
import { toast } from "sonner";
import {
  Loader2,
  X,
  Plus,
  Trash2,
  FileText,
  Image,
  Paperclip,
  Upload,
  ExternalLink,
  Search,
  Check,
  ChevronsUpDown
} from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from "../ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { API_BASE } from "../../utils/api";
import api from "../../utils/api";
import { cn } from "../ui/utils";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";

interface QualificationFormProps {
  initialData?: Qualification | null;
  personId?: string;
  onSubmit: (data: any, files: File[]) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  submitLabel?: string;
  isPage?: boolean;
  hidePersonSelection?: boolean;
  mode?: "add" | "edit";
}

export const QualificationForm: React.FC<QualificationFormProps> = ({
  initialData,
  personId,
  onSubmit,
  onCancel,
  isLoading = false,
  submitLabel = "Save Qualification",
  isPage = false,
  hidePersonSelection = false,
  mode
}) => {
  const { users, selectionUsers, qualificationTypes, qualificationPresets, fetchQualificationTypes, fetchQualificationPresets } = useData();
  const ability = useAbility(AbilityContext);
  const currentMode = mode || (initialData ? "edit" : "add");
  const action = currentMode === "add" ? ACTIONS.CREATE : ACTIONS.UPDATE;
  const inputId = useId();

  const getFileIcon = (mimetype?: string) => {
    if (mimetype?.includes('pdf')) return <FileText className="h-4 w-4 text-red-500 shrink-0" />;
    if (mimetype?.includes('image')) return <Image className="h-4 w-4 text-blue-500 shrink-0" />;
    return <Paperclip className="h-4 w-4 text-slate-400 shrink-0" />;
  };

  const [formData, setFormData] = useState<Partial<Qualification>>({
    name: "",
    issuingAuthority: "",
    issueDate: "",
    expiryDate: "",
    courseNumber: "",
    provider: "",
    contactPhone: "",
    requiresRenewal: false,
    reminderIntervals: [30, 14, 7],
    reminderRecipients: [],
    isActive: true,
  });
  const [costValue, setCostValue] = useState<string>("0");
  const [customInterval, setCustomInterval] = useState<string>("");
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [selectedTypeId, setSelectedTypeId] = useState<string>("");
  const [selectedPresetId, setSelectedPresetId] = useState<string>("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<any[]>([]);
  const [recipientSearchTerm, setRecipientSearchTerm] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    console.log("🛠️ QualificationForm Diagnostic: Version 2026-04-10-v5. Native Label Trigger Active.");
    if (qualificationTypes && qualificationTypes.length === 0 && fetchQualificationTypes) fetchQualificationTypes();
    if (qualificationPresets && qualificationPresets.length === 0 && fetchQualificationPresets) fetchQualificationPresets();
  }, [qualificationTypes?.length, qualificationPresets?.length]);

  const [personSearchOpen, setPersonSearchOpen] = useState(false);

  // Categorize users into System and Field
  const groupedUsers = useMemo(() => {
    // Fallback to 'users' if 'selectionUsers' is not yet loaded or empty
    const sourceUsers = (selectionUsers && selectionUsers.length > 0) ? selectionUsers : (users || []);
    // Archived users are never selectable (selectionUsers already excludes them; the fallback list may not)
    const activeUsers = sourceUsers.filter(u => !u.archived);

    return {
      system: activeUsers.filter(u => u.userRole && SYSTEM_ROLES.includes(u.userRole)),
      field: activeUsers.filter(u => u.userRole && FIELD_ROLES.includes(u.userRole)),
      other: activeUsers.filter(u => u.userRole && !SYSTEM_ROLES.includes(u.userRole) && !FIELD_ROLES.includes(u.userRole))
    };
  }, [users, selectionUsers]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        issuingAuthority: initialData.issuingAuthority,
        issueDate: initialData.issueDate ? new Date(initialData.issueDate).toISOString().split('T')[0] : "",
        expiryDate: initialData.expiryDate ? new Date(initialData.expiryDate).toISOString().split('T')[0] : "",
        courseNumber: initialData.courseNumber,
        provider: initialData.provider,
        contactPhone: initialData.contactPhone || "",
        requiresRenewal: initialData.requiresRenewal,
        reminderIntervals: initialData.reminderIntervals || [30, 14, 7],
        reminderRecipients: initialData.reminderRecipients || [],
        isActive: initialData.isActive,
      });
      setCostValue(initialData.cost?.toString() || "0");
      setSelectedUserId(initialData.person?.id ? initialData.person.id.toString() : "");

      const currentTypeId = initialData.type?.id || (initialData as any).typeId;
      setSelectedTypeId(currentTypeId ? currentTypeId.toString() : "");

      const currentPresetId = initialData.preset?.id || (initialData as any).presetId;
      setSelectedPresetId(currentPresetId ? currentPresetId.toString() : "");

      setExistingAttachments(initialData.attachments || []);
    } else {
      setFormData({
        name: "",
        issuingAuthority: "",
        issueDate: "",
        expiryDate: "",
        courseNumber: "",
        provider: "",
        contactPhone: "",
        requiresRenewal: false,
        reminderIntervals: [30, 14, 7],
        reminderRecipients: [],
        isActive: true,
      });
      setCostValue("0");
      setSelectedUserId(personId ? personId.toString() : "");
      setSelectedTypeId("");
      setSelectedPresetId("");

      api.get("/api/qualifications/defaults").then(response => {
        if (response.success && response.defaults) {
          setFormData(prev => ({
            ...prev,
            reminderRecipients: response.defaults.defaultRecipients || [],
            reminderIntervals: response.defaults.defaultIntervals || [30, 14, 7]
          }));
        }
      });
    }
  }, [initialData, personId]);

  const handleApplyPreset = (presetId: string) => {
    setSelectedPresetId(presetId);
    const preset = qualificationPresets.find(p => p.id === presetId);
    if (preset) {
      setFormData(prev => ({
        ...prev,
        name: preset.name,
        issuingAuthority: preset.issuingAuthority || prev.issuingAuthority
      }));
      if (preset.type) {
        setSelectedTypeId(preset.type.id.toString());
      }
    }
  };

  const handleCostChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    if (costValue === "0" && val.length > 1 && val[0] === "0" && val[1] !== ".") {
      val = val.substring(1);
    }
    setCostValue(val);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log("📂 File input change event triggered in handleFileChange");
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      console.log(`📎 Selected ${newFiles.length} files:`, newFiles.map(f => f.name));
      const MAX_SIZE = 10 * 1024 * 1024;
      const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
      const validFiles = newFiles.filter(f => f.size <= MAX_SIZE && ALLOWED_TYPES.includes(f.type));
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
  };

  const removeSelectedFile = (index: number, e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleAttachmentAction = async (attachment: { id: any; filename: string }, mode: 'view' | 'download') => {
    if (!attachment?.id) return;

    try {
      const token = localStorage.getItem("token");
      const actionUrl = `${API_BASE}/api/qualifications/attachments/${attachment.id}/download?mode=${mode}&format=json`;

      const response = await fetch(actionUrl, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      let fileUrl: string;
      let filename = attachment.filename || 'download';
      let isBlobUrl = false;

      const contentType = response.headers.get('Content-Type') || '';
      if (contentType.includes('application/json')) {
        // S3-backed attachment: the backend returns a fresh signed URL generated
        // by the S3 helper. Never use the stored attachment.url — it is a
        // pre-signed URL created at upload time that expires after 1 hour.
        const result = await response.json();
        if (!response.ok || !result.success || !result.downloadUrl) {
          throw new Error(result?.message || `Failed to ${mode} attachment`);
        }
        fileUrl = result.downloadUrl;
        filename = result.filename || filename;
      } else {
        // Legacy local file: the backend streams the file directly
        if (!response.ok) throw new Error(`Failed to ${mode} attachment`);
        const blob = await response.blob();
        fileUrl = window.URL.createObjectURL(blob);
        isBlobUrl = true;
      }

      if (mode === 'view') {
        // For viewing, open in a new tab
        window.open(fileUrl, '_blank');
        // Note: blob URLs are not revoked immediately so the new tab can load
      } else {
        // For downloading, create a temporary link to trigger native download
        const link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', filename);
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        if (isBlobUrl) window.URL.revokeObjectURL(fileUrl);
      }
    } catch (error) {
      console.error(`${mode} failed:`, error);
      toast.error(`Failed to ${mode} file`);
    }
  };

  const deleteAttachment = async (attachmentId: string, e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();

    try {
      await api.delete(`/api/qualifications/attachments/${attachmentId}`);
      setExistingAttachments(prev => (prev || []).filter(a => a.id !== attachmentId));
      toast.success("Attachment removed");
    } catch (error) {
      console.error("Delete attachment error:", error);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields marked with * in UI
    if (!selectedUserId) {
      toast.error("Please select a staff member");
      return;
    }
    if (!formData.name) {
      toast.error("Qualification name is required");
      return;
    }
    if (!selectedTypeId) {
      toast.error("Qualification type is required");
      return;
    }
    if (!formData.issuingAuthority) {
      toast.error("Issuing authority is required");
      return;
    }
    if (!formData.issueDate) {
      toast.error("Issue date is required");
      return;
    }

    // Attachment validation
    if (existingAttachments.length === 0 && selectedFiles.length === 0) {
      toast.error("At least one attachment is required for this qualification.");
      return;
    }

    // Prepare payload and clean up empty date strings to prevent PostgreSQL syntax errors
    const payload = {
      ...formData,
      issueDate: formData.issueDate || null,
      expiryDate: formData.expiryDate || null,
      cost: parseFloat(costValue) || 0,
      personId: selectedUserId,
      typeId: selectedTypeId || null,
      presetId: selectedPresetId || null,
    };

    onSubmit(payload, selectedFiles);
  };

  const containerClasses = isPage ? "space-y-8" : "space-y-6";
  const sectionClasses = isPage ? "bg-white p-6 rounded-lg border border-slate-200 shadow-sm" : "space-y-6";

  const sortedQualificationTypes = useMemo(() => {
    return [...qualificationTypes]
      .filter(t => !t.isArchived || t.id.toString() === selectedTypeId)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [qualificationTypes, selectedTypeId]);

  return (
    <form onSubmit={handleSubmit} className={containerClasses}>
      {/* Person Selection (only if not pre-selected) */}
      {!personId && !hidePersonSelection && (ability.can(ACTIONS.READ, RESOURCES.PERSON_PAGE) || ability.can(ACTIONS.READ, RESOURCES.PERSON_I_MANAGE_PAGE)) && (
        <div className={sectionClasses}>
          {isPage && <h3 className="text-lg font-semibold mb-4 text-slate-900">Select Person</h3>}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">Choose the staff member <span className="text-red-500">*</span></Label>

            <Popover open={personSearchOpen} onOpenChange={setPersonSearchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={personSearchOpen}
                  className="w-full justify-between bg-slate-50 border-slate-200 h-11 px-3"
                >
                  <span className="truncate">
                    {selectedUserId
                      ? users.find((u) => u.id.toString() === selectedUserId)?.name
                      : "Select a person..."}
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 border-slate-200 shadow-xl rounded-xl overflow-hidden" align="start" sideOffset={6} style={{ width: 'var(--radix-popover-trigger-width)' }}>
                <Command className="border-none" loop={false}>
                  <div className="p-1 border-b border-slate-100 bg-slate-50/30">
                    <CommandInput
                      placeholder="Search staff member..."
                      className="h-9 border-none focus-visible:ring-0 focus-visible:ring-offset-0 outline-none text-sm"
                    />
                  </div>
                  <CommandList className="max-h-[340px] p-1.5 custom-scrollbar">
                    <CommandEmpty className="py-6 text-center text-sm text-slate-500">No staff member found.</CommandEmpty>

                    {groupedUsers.system.length > 0 && (
                      <CommandGroup heading={<span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-1">System Users</span>} className="px-1">
                        {groupedUsers.system.map((u) => (
                          <CommandItem
                            key={u.id}
                            value={u.name}
                            onSelect={() => {
                              setSelectedUserId(u.id.toString());
                              setPersonSearchOpen(false);
                            }}
                            className="flex items-center justify-between cursor-pointer py-2.5 px-3 rounded-lg data-[selected=true]:bg-indigo-50/80 data-[selected=true]:text-indigo-900 group transition-colors"
                          >
                            <div className="flex flex-col min-w-0">
                              <span className="font-semibold text-sm text-slate-900 group-data-[selected=true]:text-indigo-900">{u.name}</span>
                              <span className="text-xs text-slate-500 group-data-[selected=true]:text-indigo-600/70">{u.userRole}</span>
                            </div>
                            <Check
                              className={cn(
                                "ml-auto h-4 w-4 text-indigo-600 transition-opacity",
                                selectedUserId === u.id.toString() ? "opacity-100" : "opacity-0"
                              )}
                            />
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}

                    {groupedUsers.system.length > 0 && groupedUsers.field.length > 0 && (
                      <div className="h-px bg-slate-100 my-1 mx-2" />
                    )}

                    {groupedUsers.field.length > 0 && (
                      <CommandGroup heading={<span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-1">Field Users</span>} className="px-1 text-slate-900">
                        {groupedUsers.field.map((u) => (
                          <CommandItem
                            key={u.id}
                            value={u.name}
                            onSelect={() => {
                              setSelectedUserId(u.id.toString());
                              setPersonSearchOpen(false);
                            }}
                            className="flex items-center justify-between cursor-pointer py-2.5 px-3 rounded-lg data-[selected=true]:bg-indigo-50/80 data-[selected=true]:text-indigo-900 group transition-colors"
                          >
                            <div className="flex flex-col min-w-0">
                              <span className="font-semibold text-sm text-slate-900 group-data-[selected=true]:text-indigo-900">{u.name}</span>
                              <span className="text-xs text-slate-500 group-data-[selected=true]:text-indigo-600/70">{u.userRole}</span>
                            </div>
                            <Check
                              className={cn(
                                "ml-auto h-4 w-4 text-indigo-600 transition-opacity",
                                selectedUserId === u.id.toString() ? "opacity-100" : "opacity-0"
                              )}
                            />
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}

                    {groupedUsers.other.length > 0 && (
                      <>
                        <div className="h-px bg-slate-100 my-1 mx-2" />
                        <CommandGroup heading={<span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-1">Other</span>} className="px-1 text-slate-900">
                          {groupedUsers.other.map((u) => (
                            <CommandItem
                              key={u.id}
                              value={u.name}
                              onSelect={() => {
                                setSelectedUserId(u.id.toString());
                                setPersonSearchOpen(false);
                              }}
                              className="flex items-center justify-between cursor-pointer py-2.5 px-3 rounded-lg data-[selected=true]:bg-indigo-50/80 data-[selected=true]:text-indigo-900 group transition-colors"
                            >
                              <div className="flex flex-col min-w-0">
                                <span className="font-semibold text-sm text-slate-900 group-data-[selected=true]:text-indigo-900">{u.name}</span>
                                <span className="text-xs text-slate-500 group-data-[selected=true]:text-indigo-600/70">{u.userRole}</span>
                              </div>
                              <Check
                                className={cn(
                                  "ml-auto h-4 w-4 text-indigo-600 transition-opacity",
                                  selectedUserId === u.id.toString() ? "opacity-100" : "opacity-0"
                                )}
                              />
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      )}

      {/* Select Qualification Type (Presets) Section - PER PNG */}
      {ability.can(ACTIONS.READ, RESOURCES.QUALIFICATION_PRESET) && (
        <div className={sectionClasses}>
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-slate-900">Select Qualification Type</h3>
            <p className="text-sm text-slate-500">Choose from preset list or enter custom</p>
            <Select value={selectedPresetId} onValueChange={handleApplyPreset}>
              <SelectTrigger className="bg-slate-50 border-slate-200 h-11">
                <SelectValue placeholder="Select a qualification." />
              </SelectTrigger>
              <SelectContent>
                {qualificationPresets.map(preset => (
                  <SelectItem key={preset.id} value={preset.id}>{preset.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Basic Information Section - PER PNG GRID */}
      {ability.can(action, RESOURCES.QUALIFICATION) && (
        <div className={sectionClasses}>
          <h3 className="text-lg font-semibold mb-4 text-slate-900">Basic Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
            {/* Row 1: Qualification Name (Full Width) */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="name" className="text-slate-700 font-medium">Qualification Name <span className="text-red-500">*</span></Label>
              <Input
                id="name"
                placeholder="e.g. High Risk Work License"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-slate-50 border-slate-200 h-11"
                required
              />
            </div>

            {/* Row 2: Type | Issuing Authority */}
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Type <span className="text-red-500">*</span></Label>
              <Select value={selectedTypeId} onValueChange={setSelectedTypeId}>
                <SelectTrigger className="bg-slate-50 border-slate-200 h-11">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {sortedQualificationTypes.map((type: any) => (
                    <SelectItem key={type.id} value={type.id.toString()}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="issuingAuthority" className="text-slate-700 font-medium">Issuing Authority <span className="text-red-500">*</span></Label>
              <Input
                id="issuingAuthority"
                placeholder="e.g. SafeWork NSW"
                value={formData.issuingAuthority}
                onChange={(e) => setFormData({ ...formData, issuingAuthority: e.target.value })}
                className="bg-slate-50 border-slate-200 h-11"
              />
            </div>

            {/* Row 3: Issue Date | Expiry Date */}
            <div className="space-y-2">
              <Label htmlFor="issueDate" className="text-slate-700 font-medium">Issue Date <span className="text-red-500">*</span></Label>
              <Input
                id="issueDate"
                type="date"
                value={formData.issueDate}
                onChange={(e) => setFormData({ ...formData, issueDate: e.target.value })}
                className="bg-slate-50 border-slate-200 h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="expiryDate" className="text-slate-700 font-medium">Expiry Date</Label>
              <Input
                id="expiryDate"
                type="date"
                value={formData.expiryDate}
                onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                className="bg-slate-50 border-slate-200 h-11"
              />
            </div>

            {/* Row 4: Course Number | Course Provider */}
            <div className="space-y-2">
              <Label htmlFor="courseNumber" className="text-slate-700 font-medium">Course Number</Label>
              <Input
                id="courseNumber"
                value={formData.courseNumber}
                onChange={(e) => setFormData({ ...formData, courseNumber: e.target.value })}
                className="bg-slate-50 border-slate-200 h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="provider" className="text-slate-700 font-medium">Course Provider</Label>
              <Input
                id="provider"
                value={formData.provider}
                onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                className="bg-slate-50 border-slate-200 h-11"
              />
            </div>

            {/* Row 5: Contact Ph | Cost */}
            <div className="space-y-2">
              <Label htmlFor="contactPhone" className="text-slate-700 font-medium">Contact Ph</Label>
              <Input
                id="contactPhone"
                value={formData.contactPhone}
                onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                className="bg-slate-50 border-slate-200 h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cost" className="text-slate-700 font-medium">Cost (excluding Taxes)</Label>
              <Input
                id="cost"
                type="number"
                value={costValue}
                onChange={handleCostChange}
                className="bg-slate-50 border-slate-200 h-11"
              />
            </div>
          </div>
        </div>
      )}

      {/* Renewal Section */}
      {ability.can(action, RESOURCES.QUALIFICATION) && (
        <div className={cn(sectionClasses, "space-y-6")}>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-2">
              <div className="space-y-0.5">
                <Label htmlFor="requiresRenewal" className="text-base font-bold text-slate-900 leading-none">Requires Renewal</Label>
                <p className="text-sm text-slate-500">Enable automatic reminders before expiry</p>
              </div>
              <Switch
                id="requiresRenewal"
                checked={formData.requiresRenewal}
                onCheckedChange={(checked) => setFormData({ ...formData, requiresRenewal: checked })}
                className="data-[state=checked]:bg-slate-900"
              />
            </div>
          </div>

          {formData.requiresRenewal && (
            <div className="space-y-8 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2 duration-300">
              {/* Reminder Schedule */}
              <div className="space-y-4">
                <Label className="text-base font-bold text-slate-900">Reminder Schedule (days before expiry)</Label>

                <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg min-h-[52px]">
                  {(formData.reminderIntervals || []).map((days) => (
                    <div key={days} className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-md shadow-sm">
                      <span className="text-sm font-medium text-slate-700">{days} days</span>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            reminderIntervals: (formData.reminderIntervals || []).filter(d => d !== days)
                          });
                        }}
                        className="text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  {(!formData.reminderIntervals || formData.reminderIntervals.length === 0) && (
                    <span className="text-sm text-slate-400 self-center px-1 italic">No reminders scheduled</span>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <div className="relative flex-1 max-w-[240px]">
                    <Input
                      placeholder="Add custom days"
                      type="number"
                      className="h-11 bg-slate-50/50 border-slate-200 focus-visible:ring-slate-900"
                      value={customInterval}
                      onChange={(e) => setCustomInterval(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const val = parseInt(customInterval);
                          if (!isNaN(val) && val > 0 && !formData.reminderIntervals?.includes(val)) {
                            setFormData({
                              ...formData,
                              reminderIntervals: [...(formData.reminderIntervals || []), val].sort((a, b) => b - a)
                            });
                            setCustomInterval("");
                          }
                        }
                      }}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11 px-5 border-slate-200 hover:bg-slate-50 font-medium"
                    onClick={() => {
                      const val = parseInt(customInterval);
                      if (!isNaN(val) && val > 0 && !formData.reminderIntervals?.includes(val)) {
                        setFormData({
                          ...formData,
                          reminderIntervals: [...(formData.reminderIntervals || []), val].sort((a, b) => b - a)
                        });
                        setCustomInterval("");
                      }
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add
                  </Button>
                </div>
              </div>

              {/* Recipients */}
              {(ability.can(ACTIONS.READ, RESOURCES.PERSON_PAGE) || ability.can(ACTIONS.READ, RESOURCES.PERSON_I_MANAGE_PAGE)) && (
                <div className="space-y-4 pt-6 border-t border-slate-100">
                  <div className="space-y-0.5">
                    <h4 className="text-base font-bold text-slate-900">Send Reminders To</h4>
                    <p className="text-sm text-slate-500">Select users who should receive reminder notifications</p>
                  </div>

                  <div className="relative max-w-sm">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search recipients by name or email..."
                      className="pl-10 h-10 bg-slate-50 border-slate-200 focus-visible:ring-slate-900"
                      value={recipientSearchTerm}
                      onChange={(e) => setRecipientSearchTerm(e.target.value)}
                    />
                    {recipientSearchTerm && (
                      <button
                        type="button"
                        onClick={() => setRecipientSearchTerm("")}
                        className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  <div className="space-y-1 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                    {selectionUsers
                      .filter(u => {
                        const search = recipientSearchTerm.toLowerCase();
                        return u.name.toLowerCase().includes(search) || u.email.toLowerCase().includes(search);
                      })
                      .map((user) => (
                        <label
                          key={user.id}
                          htmlFor={`user-check-${user.id}`}
                          className="flex items-start space-x-4 p-3 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer group"
                        >
                          <Checkbox
                            id={`user-check-${user.id}`}
                            checked={formData.reminderRecipients?.includes(user.id.toString())}
                            onCheckedChange={(checked) => {
                              const current = formData.reminderRecipients || [];
                              const next = checked
                                ? (current.includes(user.id.toString()) ? current : [...current, user.id.toString()])
                                : current.filter(id => id !== user.id.toString());
                              setFormData({ ...formData, reminderRecipients: next });
                            }}
                            className="mt-1 data-[state=checked]:bg-slate-900"
                          />
                          <div className="flex flex-col min-w-0">
                            <span className="text-sm font-bold text-slate-900">{user.name}</span>
                            <span className="text-xs text-slate-500 truncate">{user.email}</span>
                          </div>
                        </label>
                      ))}
                  </div>

                  <div className="pt-4 flex items-center justify-between">
                    <p className="text-sm text-slate-500 italic">
                      {(() => {
                        const visibleUserIds = new Set(selectionUsers.map(u => u.id.toString()));
                        const visibleSelectedCount = (formData.reminderRecipients || []).filter(id => visibleUserIds.has(id)).length;
                        return `${visibleSelectedCount} ${visibleSelectedCount === 1 ? 'user' : 'users'} selected`;
                      })()}
                    </p>
                  </div>

                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Attachments Section - ONLY if they have create/update qualification */}
      {ability.can(action, RESOURCES.QUALIFICATION) && (
        <div className={sectionClasses}>
          {isPage && (
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Attachments <span className="text-red-500">*</span></h3>
              <p className="text-xs text-slate-500 font-medium bg-slate-100 px-2 py-1 rounded">Required</p>
            </div>
          )}
          {!isPage && !hidePersonSelection && (
            <div className="flex items-center gap-2 mb-2">
              <Label className="text-sm font-medium text-slate-700">Attachments <span className="text-red-500">*</span></Label>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileChange}
            multiple
            accept=".pdf,.jpg,.jpeg,.png"
          />
          <div
            onClick={(e) => {
              e.preventDefault();
              fileInputRef.current?.click();
            }}
            className={cn(
              "group border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer block",
              "border-slate-200 hover:border-indigo-400 hover:bg-slate-50/50",
              "flex flex-col items-center justify-center gap-4"
            )}
          >
            <div className="p-3 rounded-full bg-slate-100 group-hover:bg-indigo-50 transition-colors">
              <Upload className="h-6 w-6 text-slate-400 group-hover:text-indigo-600 transition-colors" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-slate-700">Upload certificates, licenses, or supporting documents</p>
              <p className="text-xs text-slate-500 uppercase tracking-tight">PDF, JPG, PNG (max 10MB each)</p>
            </div>
          </div>

          {/* Existing & Pending Files */}
          {(existingAttachments.length > 0 || selectedFiles.length > 0) && (
            <div className="mt-6 space-y-4">
              {/* Existing Attachments */}
              {existingAttachments.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Already Attached</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {(existingAttachments || []).map((att) => (
                      <div key={att?.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 bg-white shadow-sm hover:border-indigo-100 hover:bg-indigo-50/20 transition-all duration-200 group">
                        <div
                          className="flex items-center min-w-0 cursor-pointer flex-1"
                          onClick={(e) => {
                            e.preventDefault();
                            handleAttachmentAction(att, 'view');
                          }}
                          title={`View ${att?.filename}`}
                        >
                          <div className="p-2 rounded bg-slate-50 mr-3 border border-slate-100 group-hover:bg-white transition-colors">
                            {getFileIcon(att?.mimetype)}
                          </div>
                          <span className="text-sm font-semibold text-slate-700 group-hover:text-indigo-600 truncate mr-2" title={att?.filename}>
                            {att?.filename}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50"
                            onClick={(e) => {
                              e.preventDefault();
                              handleAttachmentAction(att, 'download');
                            }}
                            title="Download"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-red-500 hover:bg-red-50"
                            onClick={(e) => {
                              e.preventDefault();
                              deleteAttachment(att?.id, e);
                            }}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Selected Files (Pending Upload) */}
              {selectedFiles.length > 0 && (
                <div className="space-y-2 pt-2 border-t border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">To Be Uploaded</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {selectedFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 rounded-lg border border-slate-100 bg-white shadow-sm">
                        <div className="flex items-center min-w-0">
                          <div className="p-2 rounded bg-slate-50 mr-3 border border-slate-100">
                            {getFileIcon(file.type)}
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="text-sm font-medium text-slate-800 truncate" title={file.name}>{file.name}</span>
                            <span className="text-[10px] text-slate-500 font-medium tracking-tighter">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-slate-400 hover:text-red-500 hover:bg-red-50"
                          onClick={() => removeSelectedFile(idx)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Footer / Actions */}
      <div className={cn(
        "flex items-center justify-end gap-3 pt-6 border-t border-slate-100",
        isPage ? "sticky bottom-0 bg-slate-50/80 backdrop-blur-sm p-4 -mx-6 -mb-6 rounded-b-lg border-t" : ""
      )}>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
          className="bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isLoading}
          className="bg-slate-900 hover:bg-slate-800 text-white shadow-lg shadow-slate-900/10 px-6"
        >
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {submitLabel}
        </Button>
      </div>
    </form>
  );
};
